import type { AgentClient } from "./index";
import type { ChatTurn, FileChip } from "./responses";

/** How many case files can be attached at once; the compare specialist reads them whole. */
export const MAX_FILE_CHIPS = 3;

// How a context change note rides along with the next message, so the model knows its earlier turns
// were about something else. Restored history carries it too, and is turned back into a divider.
const CONTEXT_NOTE = /^\[Context change: ([^\]]*)\]\n\n/;

// The files attached to a message, listed with the numbers the user refers to them by: `#1 = <fileReference> "<label>"`.
// Sent with every message while the chips are there, so the agent never relies on an earlier turn's numbering.
const FILES_NOTE = /^\[Files(?: \(([^)]*)\))?: ([^\]]*)\]\n\n/;
const FILE_ITEM = /#(\d+) = (\S+) "([^"]*)"/g;
const RENUMBERED = "file references changed; earlier # references no longer apply";
const RENUMBERED_DIVIDER = "File references changed. Earlier # references no longer apply.";

function withContextNote(input: string, note: string | undefined): string {
  return note ? `[Context change: ${note}]\n\n${input}` : input;
}

function withFilesNote(input: string, chips: readonly FileChip[], renumbered: boolean): string {
  if (chips.length === 0) return input;
  const list = chips.map((chip) => `#${chip.n} = ${chip.fileReference} "${chip.label}"`).join("; ");
  return `[Files${renumbered ? ` (${RENUMBERED})` : ""}: ${list}]\n\n${input}`;
}

function dividerTurn(text: string): ChatTurn {
  return { role: "note", events: [{ kind: "text", text }] };
}

// Splits user turns that carried a context or files note back into the dividers, the chips and the message as typed.
function withNoteDividers(turns: ChatTurn[]): ChatTurn[] {
  return turns.flatMap((turn) => {
    const first = turn.events[0];
    if (turn.role !== "user" || first?.kind !== "text") return [turn];

    let text = first.text;
    const dividers: ChatTurn[] = [];
    const context = CONTEXT_NOTE.exec(text);
    if (context) {
      dividers.push(dividerTurn(context[1]));
      text = text.slice(context[0].length);
    }
    const files = FILES_NOTE.exec(text);
    if (files) {
      if (files[1]) dividers.push(dividerTurn(RENUMBERED_DIVIDER));
      text = text.slice(files[0].length);
    }
    if (!context && !files) return [turn];

    const attached = files
      ? [...files[2].matchAll(FILE_ITEM)].map((m) => ({ n: Number(m[1]), fileReference: m[2], label: m[3] }))
      : [];
    return [
      ...dividers,
      {
        ...turn,
        events: [{ kind: "text", text }, ...turn.events.slice(1)],
        ...(attached.length > 0 ? { files: attached } : {}),
      },
    ];
  });
}

// Labels sit inside the quoted, bracketed `[Files: ...]` line, so nothing that could end it is kept.
function cleanLabel(label: string): string {
  return (
    label
      .replace(/["[\];\r\n]+/g, " ")
      .replace(/\s+/g, " ")
      .trim() || "File"
  );
}

export interface ChatState {
  readonly turns: readonly ChatTurn[];
  /** A turn (or a restore) is in flight. */
  readonly sending: boolean;
  /** Reloading the conversation already bound to the session, e.g. after a page refresh. */
  readonly restoring: boolean;
  readonly error: string | null;
  /** The Viewer session the chat is bound to; nothing can be sent until there is one. */
  readonly sessionLinkId: string | undefined;
  /** Case files attached to every message until removed. */
  readonly chips: readonly FileChip[];
}

/**
 * The chat bound to one Viewer session (sessionLinkId), independent of any UI framework. State is
 * replaced, never mutated, on every change, so it can be read with React's useSyncExternalStore
 * or held in a Vue shallowRef from subscribe().
 */
export function createChatSession(client: AgentClient) {
  let state: ChatState = {
    turns: [],
    sending: false,
    restoring: false,
    error: null,
    sessionLinkId: undefined,
    chips: [],
  };
  const listeners = new Set<() => void>();
  let previousResponseId: string | undefined;
  // The agent echoes the session link back once it has bound this conversation to it; until then
  // every turn carries the session link so the agent can (re)try the binding.
  let linked = false;
  // A context change the agent hasn't been told about yet; it goes with the next message.
  let pendingNote: string | undefined;
  // The numbering the agent was last given (number -> fileReference), to tell when it has changed under it.
  let sentFiles = new Map<number, string>();

  function update(changes: Partial<ChatState>) {
    state = { ...state, ...changes };
    listeners.forEach((listener) => listener());
  }

  /**
   * Switches the chat to another session (or none), clearing the current conversation. resumed
   * means the session may already have a conversation bound to it, which is then restored.
   */
  function setSessionLink(sessionLinkId: string | undefined, resumed: boolean) {
    linked = false;
    previousResponseId = undefined;
    pendingNote = undefined;
    sentFiles = new Map();
    client.clearConversationKey();
    update({ turns: [], error: null, sessionLinkId, chips: [] });
    if (sessionLinkId && resumed) void restore(sessionLinkId);
  }

  // Reloads the chat already bound to this session from Foundry's stored responses. The key is
  // re-pointed before the turns are published, as MCP App cards read it when they mount.
  async function restore(sessionLinkId: string) {
    update({ sending: true, restoring: true });
    try {
      const lastResponseId = await client.session.getLastResponse(sessionLinkId);
      if (!lastResponseId || state.sessionLinkId !== sessionLinkId) return;
      const restored = await client.loadConversation(lastResponseId);
      if (state.sessionLinkId !== sessionLinkId) return;
      client.resumeConversationKey(lastResponseId);
      previousResponseId = lastResponseId;
      linked = true;
      update({ turns: withNoteDividers(restored) });
    } catch (e) {
      if (state.sessionLinkId !== sessionLinkId) return;
      const reason = e instanceof Error ? e.message : String(e);
      update({ error: `The previous conversation could not be restored: ${reason}` });
    } finally {
      update({ sending: false, restoring: false });
    }
  }

  /** Sends a message; displayText is what the user's turn shows, if different from what the agent receives. */
  async function send(input: string, displayText = input) {
    if (state.sending) return;
    const sessionLinkId = state.sessionLinkId;
    if (!sessionLinkId) {
      update({ error: "The Viewer session is not ready. Reload the case before sending a message." });
      return;
    }

    // Numbers the agent was given before that now mean something else: say so, to the user and the agent.
    const chips = state.chips;
    const renumbered =
      chips.length > 0 &&
      [...sentFiles].some(([n, fileReference]) => chips.find((chip) => chip.n === n)?.fileReference !== fileReference);

    update({
      turns: [
        ...state.turns,
        ...(renumbered ? [dividerTurn(RENUMBERED_DIVIDER)] : []),
        {
          role: "user",
          events: [{ kind: "text", text: displayText }],
          ...(chips.length > 0 ? { files: [...chips] } : {}),
        },
      ],
      sending: true,
      error: null,
    });

    try {
      const note = pendingNote;
      const response = await client.sendMessage(
        withContextNote(withFilesNote(input, chips, renumbered), note),
        previousResponseId,
        linked ? undefined : sessionLinkId,
      );
      // The agent has it now, even if the turn itself failed below.
      if (pendingNote === note) pendingNote = undefined;
      sentFiles = new Map(chips.map((chip) => [chip.n, chip.fileReference]));
      if (state.sessionLinkId !== sessionLinkId) {
        client.clearConversationKey();
        return;
      }
      const { responseId, events, usage, linkedSessionLinkId } = response;
      previousResponseId = responseId;
      if (linkedSessionLinkId === sessionLinkId) linked = true;
      if (response.error) throw new Error(response.error);
      if (!linked) throw new Error("The agent did not confirm the case link. Send your message again to retry.");
      update({ turns: [...state.turns, { role: "assistant", events, usage }] });
      const conversationKey = client.getConversationKey();
      if (conversationKey) {
        void client.session.setLastResponse(sessionLinkId, conversationKey, responseId).catch(console.warn);
      }
    } catch (e) {
      if (state.sessionLinkId !== sessionLinkId) return;
      update({ error: e instanceof Error ? e.message : String(e) });
    } finally {
      update({ sending: false });
    }
  }

  /**
   * Tells the agent, with the next message, that what the user is looking at has changed (e.g.
   * "Switched to case X."), and shows the same text as a divider. Successive changes before the
   * next message replace each other. Nothing to tell if the conversation hasn't started.
   */
  function noteContextChange(note: string) {
    if (state.turns.length === 0) return;
    pendingNote = note;
    const turns = state.turns.at(-1)?.role === "note" ? state.turns.slice(0, -1) : state.turns;
    update({ turns: [...turns, { role: "note", events: [{ kind: "text", text: note }] }] });
  }

  /** Attaches a case file to every message from now on, as the next number (#1, #2, ...). */
  function addChip(fileReference: string, label: string) {
    if (state.chips.length >= MAX_FILE_CHIPS || state.chips.some((chip) => chip.fileReference === fileReference))
      return;
    update({ chips: [...state.chips, { n: state.chips.length + 1, fileReference, label: cleanLabel(label) }] });
  }

  /** Detaches a file; the files after it move up a number. */
  function removeChip(fileReference: string) {
    update({
      chips: state.chips
        .filter((chip) => chip.fileReference !== fileReference)
        .map((chip, i) => ({ ...chip, n: i + 1 })),
    });
  }

  /** Drops every file, e.g. when the case changes, as the references only mean something inside their case. */
  function clearChips() {
    sentFiles = new Map();
    if (state.chips.length > 0) update({ chips: [] });
  }

  /** Shows a reply from the host itself (e.g. confirming a local command) without involving the agent. */
  function addLocalReply(text: string) {
    update({ turns: [...state.turns, { role: "assistant", events: [{ kind: "text", text }] }] });
  }

  return {
    getState: () => state,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
    setSessionLink,
    send,
    noteContextChange,
    addLocalReply,
    addChip,
    removeChip,
    clearChips,
  };
}

export type ChatSession = ReturnType<typeof createChatSession>;
